import React, { useState } from 'react'
import { Chart } from 'react-google-charts'
import { useSkills } from '../SkillsProvider'
import { cn } from '../../../lib/utils'
import { useTranslation } from '../../../hooks/useTranslation'

const GraphView = () => {
  const { t } = useTranslation('')
  const {
    skillsArr,
    setSelectedSkill,
    openCardViewTab,
    selectedSkill,
    isMobile,
    addSkillstoSkillsDialog,
  } = useSkills()

  const [popUpText, setPopUpText] = useState()

  const [chartLoaded, setChartLoaded] = useState(false)

  const categoryColors = {
    relationship: '#527bad',
    communication: '#396479',
    leadership: '#95499c',
    analytical: '#9c4959',
    creative: '#499c65',
    physical: '#54a5ab',
    technical: '#a47841',
  }

  const chartColumns = [
    { type: 'number', id: t('enjoyment_of_skill') },
    { type: 'number', id: t('skill_level') },
    { type: 'string', role: 'style' },
    { type: 'string', role: 'tooltip' },
    { type: 'string', role: 'annotation' },
  ]

  const skillsArrDuplicate = (skill) => {
    const dupSkills = []
    skillsArr.forEach((s) => {
      if (
        s[1] !== skill[1] &&
        parseInt(s[2]) === parseInt(skill[2]) &&
        parseInt(s[3]) === parseInt(skill[3])
      ) {
        dupSkills.push(s[1])
      }
    })
    if (dupSkills.length > 0) {
      dupSkills.push(skill[1])
    }

    return dupSkills
  }

  const chartRows = skillsArr.map((skill) => {
    const [category, skillName, ability, enjoyment] = skill
    const duplicateSkills = skillsArrDuplicate(skill)
    const duplicateSkillExists = duplicateSkills.length > 0
    const pointSize =
      skill === selectedSkill ? '25' : duplicateSkillExists ? '15' : '10'
    const fillColor = duplicateSkillExists
      ? '#000000'
      : categoryColors[category] || '#CCCCCC'

    const annotation = duplicateSkillExists
      ? duplicateSkills.join(', ')
      : skillName

    const color = duplicateSkillExists ? '#000000' : '#CCCCCC'

    return [
      enjoyment - 50,
      ability - 50,
      `point { size: ${pointSize}; fill-color: ${fillColor}; color: ${color}`,
      annotation,
      null,
    ]
  })

  const annotations = []

  const chartData = [chartColumns, ...chartRows, ...annotations]

  const options = {
    hAxis: {
      title: t('enjoyment_of_skill'),
      minValue: -50,
      maxValue: 50,
      textPosition: 'none',
      titleTextStyle: {
        fontSize: 16,
        bold: true,
        italic: true,
        fontName: 'Roboto',
      },
    },
    vAxis: {
      title: t('skill_level'),
      minValue: -50,
      maxValue: 50,
      textPosition: 'none',
      titleTextStyle: {
        fontSize: 16,
        bold: true,
        italic: true,
        fontName: 'Roboto',
      },
    },
    legend: 'none',
    scale: 10,
    sizeAxis: { minValue: 0, maxSize: 10 },
    colors: Object.values(categoryColors),
    pointSize: 20,
    chartArea: { width: '90%', height: '90%' },
    annotations: {
      stem: { length: 0 },
      textStyle: {
        color: '#000',
        bold: false,
        italic: false,
        enableInteractivity: false,
        fontName: 'Roboto',
      },
    },
  }

  const handleSelect = (row) => {
    const skills = chartRows[row][3].split(', ')
    const skillsFromSkillsArr = skillsArr.filter((s) => skills.includes(s[1]))
    if (skillsFromSkillsArr.length === 1) {
      setSelectedSkill(skillsFromSkillsArr[0])
      openCardViewTab()
    } else addSkillstoSkillsDialog(skillsFromSkillsArr)
  }

  const chartEvents = [
    {
      eventName: 'select',
      callback({ chartWrapper }) {
        handleSelect(chartWrapper.getChart().getSelection()[0].row)
      },
    },
    {
      eventName: 'ready',
      callback() {
        setChartLoaded(true)
      },
    },
  ]

  return (
    <div className="relative h-[585px] py-4 md:col-span-2 md:py-0">
      {chartLoaded && (
        <>
          <AxisLabel
            variant={'vertical-up'}
            className="left-[37%] top-[19%] md:top-[15%]"
          >
            {t('expert_ability')}
            <span className="glyphicon glyphicon-arrow-right"></span>
          </AxisLabel>
          <AxisLabel
            variant={'vertical-down'}
            className={'bottom-[13%] left-[41%] md:bottom-[29%] md:left-[38%]'}
          >
            {t('no_ability')}
            <span className="glyphicon glyphicon-arrow-right"></span>
          </AxisLabel>
          <AxisLabel className={' left-[5%] top-[53%] md:top-[42%]'}>
            <span className="glyphicon glyphicon-arrow-left"></span>
            {t('dont_care_for_it')}
          </AxisLabel>
          <AxisLabel
            className={'right-[6%] top-[53%] md:right-1/4 md:top-[42%]'}
          >
            {t('really_enjoy')}
            <span className="glyphicon glyphicon-arrow-right"></span>
          </AxisLabel>
          <SkillLabelText
            className="left-[6%] top-[1%] md:-top-2 md:left-[15%]"
            onMouseEnter={() => setPopUpText('reskill')}
            onMouseLeave={() => setPopUpText()}
          >
            {t('reskill_motivated')}
          </SkillLabelText>
          <SkillLabelText
            className="right-[4%] top-[1%] md:-top-2 md:right-[33%]"
            onMouseEnter={() => setPopUpText('promoter')}
            onMouseLeave={() => setPopUpText()}
          >
            {t('skill_promoter')}
          </SkillLabelText>
          <SkillLabelText
            className="-bottom-[3%] left-[5%] md:bottom-[6rem] md:left-[15%]"
            onMouseEnter={() => setPopUpText('detractor')}
            onMouseLeave={() => setPopUpText()}
          >
            {t('skill_detractor')}
          </SkillLabelText>
          <SkillLabelText
            className="-bottom-[3%] right-[3%] md:bottom-[6rem] md:right-[33%]"
            onMouseEnter={() => setPopUpText('upskill')}
            onMouseLeave={() => setPopUpText()}
          >
            {t('upskill_motivated')}
          </SkillLabelText>
        </>
      )}
      {popUpText === 'reskill' && <ReskillMotivatedText />}
      {popUpText === 'promoter' && <SkillPromoterText />}
      {popUpText === 'detractor' && <SkillDetractorText />}
      {popUpText === 'upskill' && <UpskillMotivatedText />}
      <Chart
        chartType="ScatterChart"
        data={chartData}
        options={options}
        height={isMobile ? '100%' : '90%'}
        width={isMobile ? '100%' : '90%'}
        chartEvents={chartEvents}
      />
    </div>
  )
}
export default GraphView

const ReskillMotivatedText = () => {
  const { t } = useTranslation('')
  return (
    <SkillText className="left-[5%] top-[7%] h-[50%] md:left-[4%] md:top-[4%] md:h-[40%]">
      <p>{t('ReskillMotivatedText.paragraph_1')}</p>
      <p>{t('ReskillMotivatedText.paragraph_2')}</p>
    </SkillText>
  )
}

const SkillPromoterText = () => {
  const { t } = useTranslation('')
  return (
    <SkillText className="right-[5%] top-[7%] h-[50%] md:right-[23%] md:top-[4%] md:h-[40%]">
      <p>{t('SkillPromoterText.paragraph_1')}</p>
      <p>{t('SkillPromoterText.paragraph_2')}</p>
    </SkillText>
  )
}

const SkillDetractorText = () => {
  const { t } = useTranslation('')
  return (
    <SkillText className="bottom-[7%] left-[5%] h-[70%] md:bottom-32 md:left-[4%] md:h-[40%]">
      <p>{t('SkillDetractorText.paragraph_1')}</p>
      <p>{t('SkillDetractorText.paragraph_2')}</p>
    </SkillText>
  )
}

const UpskillMotivatedText = () => {
  const { t } = useTranslation('')
  return (
    <SkillText className="bottom-[7%] right-[5%] h-[58%] md:bottom-32 md:right-[23%] md:h-[40%]">
      <p>{t('UpskillMotivatedText.paragraph_1')}</p>
      <p>{t('UpskillMotivatedText.paragraph_2')}</p>
    </SkillText>
  )
}

const SkillText = ({ className, children }) => {
  return (
    <div
      className={cn(
        'absolute z-50 flex h-[40%] w-[45%] flex-col items-center justify-center bg-[#666] px-[4%] py-[2%] text-sm text-[#fff] md:w-[37%]',
        className,
      )}
    >
      {children}
    </div>
  )
}

const SkillLabelText = ({ className, children, ...props }) => {
  return (
    <div
      {...props}
      className={cn(
        'absolute z-50 flex cursor-pointer items-center gap-2',
        className,
      )}
    >
      <div className="underline">{children}</div>
      <span className="glyphicon glyphicon-plus"></span>
    </div>
  )
}

const AxisLabel = ({ variant = 'horizontal', className, children }) => {
  let transform
  if (variant === 'vertical-up') {
    transform = '-rotate-90'
  } else if (variant === 'vertical-down') {
    transform = 'rotate-90'
  } else {
    transform = ''
  }
  return (
    <div
      className={cn(
        'absolute z-50 flex items-center gap-2',
        transform,
        className,
      )}
    >
      {children}
    </div>
  )
}
